import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompetitorAnalysis = () => {
  const competitors = [
    {
      name: "WordPress.com",
      domain: "wordpress.com",
      domainAuthority: 94,
      organicKeywords: "2.4M",
      organicTraffic: "45.2M",
      backlinks: "8.9M",
      topKeywords: ["blog platform", "website builder", "cms software"],
      trend: "stable"
    },
    {
      name: "Wix",
      domain: "wix.com",
      domainAuthority: 91,
      organicKeywords: "1.8M",
      organicTraffic: "38.7M",
      backlinks: "5.2M",
      topKeywords: ["website creator", "online store", "portfolio website"],
      trend: "up"
    },
    {
      name: "Squarespace",
      domain: "squarespace.com",
      domainAuthority: 89,
      organicKeywords: "987K",
      organicTraffic: "22.1M",
      backlinks: "3.8M",
      topKeywords: ["website design", "ecommerce platform", "template builder"],
      trend: "up"
    },
    {
      name: "Webflow",
      domain: "webflow.com",
      domainAuthority: 85,
      organicKeywords: "654K",
      organicTraffic: "15.3M",
      backlinks: "2.1M",
      topKeywords: ["no code builder", "responsive design", "web development"],
      trend: "up"
    }
  ];

  const getAuthorityColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-50';
    if (score >= 80) return 'text-blue-600 bg-blue-50';
    if (score >= 70) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Icon name="Users" size={20} className="text-orange-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Competitor Analysis</h2>
            <p className="text-sm text-gray-600">Track and compare your competition</p>
          </div>
        </div>
        <Button variant="outline" iconName="Plus" iconPosition="left">
          Add Competitor
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {competitors?.map((competitor, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1">{competitor?.name}</h3>
                <p className="text-xs md:text-sm text-gray-500">{competitor?.domain}</p>
              </div>
              <div className="flex items-center gap-2">
                <Icon
                  name={competitor?.trend === 'up' ? 'TrendingUp' : 'Minus'}
                  size={18}
                  className={competitor?.trend === 'up' ? 'text-green-600' : 'text-gray-400'}
                />
                <Button variant="ghost" iconName="ExternalLink" size="sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Award" size={16} className="text-gray-600" />
                  <span className="text-xs text-gray-600">Domain Authority</span>
                </div>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-bold ${getAuthorityColor(competitor?.domainAuthority)}`}>
                  {competitor?.domainAuthority}
                </span>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Key" size={16} className="text-gray-600" />
                  <span className="text-xs text-gray-600">Keywords</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{competitor?.organicKeywords}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="BarChart3" size={16} className="text-gray-600" />
                  <span className="text-xs text-gray-600">Traffic</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{competitor?.organicTraffic}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Link" size={16} className="text-gray-600" />
                  <span className="text-xs text-gray-600">Backlinks</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{competitor?.backlinks}</p>
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-700 mb-2">Top Keywords:</p>
              <div className="flex flex-wrap gap-2">
                {competitor?.topKeywords?.map((keyword, kidx) => (
                  <span
                    key={kidx}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Target" size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm md:text-base font-semibold text-purple-900 mb-1">Competitive Insight</h4>
            <p className="text-xs md:text-sm text-purple-800">
              Your competitors are ranking for an average of 1.2M keywords. Focus on long-tail keywords and niche topics to capture untapped search opportunities.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorAnalysis;